import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeftIcon, MagnifyingGlassIcon, ArrowPathIcon, DevicePhoneMobileIcon,
  DeviceTabletIcon, CheckCircleIcon, ExclamationTriangleIcon,
  ShieldCheckIcon, ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { usePhones, useSyncPhones } from '../hooks/usePhones';

// Phone / tablet register — the mobile estate pulled nightly from Miradore (MDM).
// Read-focused: holder, serial/IMEI, OS and last check-in all come from the
// sync, so this screen is about *seeing* the fleet, not editing it. Sibling of
// the (laptop) Device Register; both live in the tech `devices` table.

const STALE_DAYS = 30;

// UK format everywhere (DD/MM/YYYY) — store ISO, format on display.
function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function daysAgo(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 864e5);
}

// "Android 16" / "iOS 18.3" → a compact platform chip.
function PlatformChip({ os }) {
  const p = /ios|iphone|ipad/i.test(os || '') ? 'iOS' : /android/i.test(os || '') ? 'Android' : 'Other';
  const cls = p === 'iOS' ? 'bg-gray-100 text-gray-700 ring-gray-500/30' : p === 'Android' ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/30' : 'bg-gray-100 text-gray-500 ring-gray-400/30';
  return <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}>{os || p}</span>;
}

function Stat({ label, value, tone = 'default' }) {
  const tones = {
    default: 'text-gray-900', brand: 'text-brand', amber: 'text-amber-600', rose: 'text-rose-600', gray: 'text-gray-400',
  };
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className={`text-2xl font-semibold tabular-nums ${tones[tone]}`}>{value}</div>
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
    </div>
  );
}

export default function PhoneRegister() {
  const { data, isLoading, error } = usePhones();
  const sync = useSyncPhones();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | phone | tablet | unassigned | stale
  const [message, setMessage] = useState(null);

  const devices = useMemo(() => data?.devices || [], [data]);
  const summary = data?.summary;

  const runSync = async () => {
    setMessage(null);
    try {
      const r = await sync.mutateAsync();
      const m = r.miradore || {};
      setMessage(m.error
        ? `Miradore sync failed: ${m.error}`
        : `Synced — ${m.created || 0} new, ${m.updated || 0} updated, ${m.assigned || 0} matched to staff${m.retired ? `, ${m.retired} retired (removed from Miradore)` : ''}.`);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Sync failed.');
    }
  };

  const rows = useMemo(() => {
    const t = search.trim().toLowerCase();
    return devices.filter((d) => {
      if (filter === 'phone' && d.device_type !== 'phone') return false;
      if (filter === 'tablet' && d.device_type !== 'tablet') return false;
      if (filter === 'unassigned' && d.assigned_entra_user_id) return false;
      if (filter === 'stale') { const da = daysAgo(d.last_seen_at); if (da !== null && da <= STALE_DAYS) return false; }
      if (filter === 'nopolicy' && d.config_policy_count) return false;
      if (!t) return true;
      return [d.assigned_name, d.make_model, d.serial_number, d.imei, d.os_version].some((v) => String(v ?? '').toLowerCase().includes(t));
    });
  }, [devices, search, filter]);

  if (isLoading) return <div className="p-8"><p className="text-sm text-gray-400">Loading phones…</p></div>;
  if (error) {
    return (
      <div className="p-8">
        <p className="text-sm text-rose-700">
          {error.response?.status === 403 ? 'The phone register is restricted to Logistics.' : 'Could not load the phone register.'}
        </p>
      </div>
    );
  }

  const FILTERS = [
    ['all', `All (${summary.total})`], ['phone', `Phones (${summary.phones})`], ['tablet', `Tablets (${summary.tablets})`],
    ['stale', `Stale (${summary.stale})`], ['nopolicy', `No policy (${summary.noPolicy || 0})`],
  ];

  const renderRow = (d, isSpare) => {
    const da = daysAgo(d.last_seen_at);
    const stale = da === null || da > STALE_DAYS;
    const Icon = d.device_type === 'tablet' ? DeviceTabletIcon : DevicePhoneMobileIcon;
    return (
      <tr key={d.id} className="border-t border-gray-100 hover:bg-gray-50">
        {!isSpare ? (
          <td className="px-3 py-2">
            {d.assigned_name
              ? <span className="font-medium text-gray-900">{d.assigned_name}</span>
              : <span className="text-xs font-medium text-amber-600">Unassigned</span>}
          </td>
        ) : null}
        <td className="px-3 py-2 text-gray-600">
          <span className="flex items-center gap-1.5">
            <Icon className="h-4 w-4 shrink-0 text-gray-400" />
            {d.make_model || '—'}
          </span>
        </td>
        <td className="px-3 py-2"><PlatformChip os={d.os_version} /></td>
        <td className="px-3 py-2 font-medium tabular-nums text-gray-900">{d.serial_number || <span className="text-gray-400">—</span>}</td>
        <td className="px-3 py-2 tabular-nums text-gray-500">{d.imei || <span className="text-gray-300">—</span>}</td>
        <td className={`px-3 py-2 whitespace-nowrap tabular-nums ${stale ? 'text-amber-600' : 'text-gray-600'}`}>
          <span className="flex items-center gap-1">
            {stale ? <ExclamationTriangleIcon className="h-3.5 w-3.5" /> : null}
            {fmtDate(d.last_seen_at)}
          </span>
        </td>
        <td className="px-3 py-2">
          {d.config_policy_count
            ? <span title={d.config_policies || ''} className="inline-flex items-center gap-1 text-xs text-gray-700"><ShieldCheckIcon className="h-4 w-4 text-emerald-600" /> {d.config_policy_count}</span>
            : <span className="inline-flex items-center gap-1 text-xs font-medium text-rose-600"><ShieldExclamationIcon className="h-4 w-4" /> None</span>}
        </td>
        <td className="px-3 py-2 text-right">
          {d.miradore_agent
            ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircleIcon className="h-4 w-4" /> Miradore</span>
            : <span className="text-xs text-gray-400">—</span>}
        </td>
      </tr>
    );
  };

  // Split the (filtered) fleet into who's holding one vs the spare/unallocated
  // pool. A phone with no Miradore holder syncs in as status 'spare', so new
  // stock enrolled without a user lands straight in the Spare table.
  const sections = [
    { key: 'inuse', label: 'In use', headers: ['Holder', 'Device', 'OS', 'Serial', 'IMEI', 'Last seen', 'Policies', ''], list: rows.filter((d) => d.status !== 'spare'), empty: 'No phones match your search.' },
    { key: 'spare', label: 'Spare / unallocated', headers: ['Device', 'OS', 'Serial', 'IMEI', 'Last seen', 'Policies', ''], list: rows.filter((d) => d.status === 'spare'), empty: 'No spare phones.' },
  ];

  return (
    <div className="p-4 sm:p-8">
      <Link to="/?dept=logistics" className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" /> Back to Logistics
      </Link>

      <div className="mb-4">
        <h1 className="text-xl font-semibold text-gray-900">Phone register</h1>
        <p className="mt-1 text-sm text-gray-500">
          Company phones &amp; tablets, synced nightly from Miradore. {summary.assigned} of {summary.total} are matched to a staff member{summary.retired ? ` · ${summary.retired} retired (removed from Miradore)` : ''}.
        </p>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
        <Stat label="Total" value={summary.total} />
        <Stat label="Phones" value={summary.phones} />
        <Stat label="Tablets" value={summary.tablets} />
        <Stat label="Assigned" value={summary.assigned} tone="brand" />
        <Stat label="Unassigned" value={summary.unassigned} tone="gray" />
        <Stat label={`Stale >${STALE_DAYS}d`} value={summary.stale} tone={summary.stale ? 'amber' : 'gray'} />
        <Stat label="No policy" value={summary.noPolicy || 0} tone={summary.noPolicy ? 'rose' : 'gray'} />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap rounded-md border border-gray-300">
          {FILTERS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setFilter(k)}
              className={`px-3 py-2 text-sm font-medium ${filter === k ? 'bg-brand text-white' : 'text-gray-700 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative min-w-[14rem] flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search holder, model, serial, IMEI…"
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
        </div>
        <button type="button" onClick={runSync} disabled={sync.isPending}
          className="flex items-center gap-1.5 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          <ArrowPathIcon className={`h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`} /> {sync.isPending ? 'Syncing…' : 'Sync now'}
        </button>
      </div>

      {message ? <p className="mb-3 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700">{message}</p> : null}

      {sections.map((sec) => (
        <div key={sec.key} className="mb-6">
          <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
            {sec.label}
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">{sec.list.length}</span>
          </h2>
          <div className="overflow-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  {sec.headers.map((h, i) => (
                    <th key={i} className="whitespace-nowrap bg-gray-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sec.list.map((d) => renderRow(d, sec.key === 'spare'))}
                {sec.list.length === 0 ? (
                  <tr><td colSpan={sec.headers.length} className="px-3 py-6 text-center text-sm text-gray-400">{sec.empty}</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <p className="mt-2 text-xs text-gray-400">Serial &amp; IMEI, OS and last check-in come from Miradore; the nightly sync refreshes them at 05:30 and auto-retires anything removed there.</p>
    </div>
  );
}
